/**
 * Shared API client for web and mobile.
 *
 * Provides typed HTTP methods that read the base URL from the
 * whitelabel config (web) or AsyncStorage-cached tenant config (native).
 * All requests include the JWT auth token from secure storage.
 *
 * Usage:
 *   import { apiClient } from '../shared/api-client';
 *   const profile = await apiClient.get<UserProfile>('/api/client/profile');
 */
import { getWhitelabelConfig, isNativeRuntime } from '../../base/whitelabel/functions';
import { getPersistentItem, getPersistentJson, getStorageGeneration } from '../../vrs-auth/storage';
import { getSecureItem, setSecureItem } from '../../vrs-auth/secureStorage';
import { isAccessTokenExpired } from '../../vrs-auth/accessTokenExpiry';
const ABSOLUTE_URL_RE = /^https?:\/\//i;
/**
 * Endpoints where a 401 IS the answer, not a symptom of an expired session.
 *
 * The refresh-and-retry below re-sends the request, which on these is a second
 * attempt at the same thing: against /2fa/challenge that spends two of the
 * challenge's limited attempts for every mistyped code — halving them, and
 * tripping "Too many attempts" while the person still has guesses left — and
 * against a login it doubles every wrong password on the auth rate limiter.
 * A device that has signed in before still holds a refresh token, so this
 * fires on exactly the sign-ins most likely to happen.
 *
 * /password/change is deliberately absent: it is authenticated, so a 401 there
 * really can mean a stale token, and retrying is right.
 *
 * The one sign-in (/sign-in) and a hand-off code (/handoff/redeem) are a
 * sign-in too: a wrong password or a spent code is the answer.
 */
const NO_REFRESH_RETRY_RE = /^\/api\/auth\/(2fa\/challenge|[a-z]+\/login|sign-in|handoff\/redeem|client\/register|refresh|password\/(forgot|reset)|otp\/(request|verify))$/;
function getBaseUrl() {
    if (!isNativeRuntime()) {
        // Web: relative URL (same origin) or read from whitelabel config
        return '';
    }
    const wl = getWhitelabelConfig();
    const cached = getPersistentJson('vrs_tenant_config');
    const domains = wl?.domains || cached?.domains;
    const appType = wl?.operations?.appType || cached?.operations?.appType;
    const domain = domains?.api || (appType === 'vrs' || appType === 'relay'
        ? domains?.clientVrs || domains?.clientVri || domains?.interpreter
        : domains?.clientVri || domains?.clientVrs || domains?.interpreter);
    if (domain) {
        return `https://${domain}`;
    }
    // Fallback: production URL
    return 'https://vrs.malkacomm.com';
}
/** The origin used for scoping durable, account-specific operations. */
export function getApiOrigin() {
    return getBaseUrl() || (typeof window !== 'undefined' ? window.location.origin : '');
}
function resolveUrl(baseUrl, path) {
    return ABSOLUTE_URL_RE.test(path) ? path : `${baseUrl}${path}`;
}
/**
 * Resolves the platform WebSocket endpoint from the same base-URL logic the
 * HTTP client uses (same-origin on web, tenant api domain on native), so
 * socket and REST traffic always target the same backend.
 *
 * @param {string} path - Socket path beginning with '/'.
 * @returns {string} Absolute ws:// or wss:// URL.
 */
export function getWebSocketUrl(path = '/ws') {
    const baseUrl = getBaseUrl();
    if (!baseUrl) {
        if (typeof window !== 'undefined') {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            return `${protocol}//${window.location.host}${path}`;
        }
        return path;
    }
    return `${baseUrl.replace(/^http/, 'ws')}${path}`;
}
function getAuthToken() {
    // Try secure storage first, then regular storage
    const raw = getSecureItem('vrs_auth_token') || getPersistentItem('vrs_auth_token');
    if (!raw) {
        return null;
    }
    try {
        const parsed = JSON.parse(raw);
        return parsed.token || raw;
    }
    catch {
        return raw;
    }
}
/**
 * The absolute URL and the auth header for a request that the system makes
 * for the app, outside fetch: the VideoMail outbox's background upload
 * (voicemail/outbox.native.ts). Null when no one is signed in.
 *
 * @param {string} path - An /api path.
 * @returns {object|null}
 */
export function authorizedRequestTarget(path) {
    const token = getAuthToken();
    if (!token) {
        return null;
    }
    return { headers: { Authorization: `Bearer ${token}` }, url: resolveUrl(getBaseUrl(), path) };
}
/** Ephemeral identity for sensitive operations; never persist or log this value. */
export function getApiSessionIdentity() {
    const token = getAuthToken();
    return token ? JSON.stringify([getApiOrigin(), token]) : null;
}
function getAuthTokenEnvelope() {
    const raw = getSecureItem('vrs_auth_token') || getPersistentItem('vrs_auth_token');
    if (!raw) {
        const user = getPersistentJson('vrs_user_info');
        const role = getPersistentItem('vrs_user_role') || user?.role;
        return user || role ? { userId: user?.id, role } : null;
    }
    try {
        return JSON.parse(raw);
    }
    catch {
        return { token: raw };
    }
}
function getRefreshToken() {
    return getSecureItem('vrs_refresh_token') || getPersistentItem('vrs_refresh_token');
}
let refreshInFlight = null;
// One verified rotation lets a slower request retry under the same session.
// A new login or another rotation must not inherit this retry authority.
let completedRefresh = null;
const REFRESH_REJECTION_CODES = new Set([
    'REFRESH_TOKEN_INVALID', 'AUTH_CHANGED', 'AUTH_STATE_CHANGED', 'AUTH_SESSION_REVOKED',
    'ACCOUNT_EXPIRED', 'ACCOUNT_INACTIVE', 'ACCOUNT_SUSPENDED',
    'DEMO_SESSION_UNAVAILABLE', 'CERTIFIED_MODE_DEMO_BLOCKED'
]);
const authSessionEndedListeners = new Set();
export function onAuthSessionEnded(listener) {
    authSessionEndedListeners.add(listener);
    return () => {
        authSessionEndedListeners.delete(listener);
    };
}
function announceAuthSessionEnded(code) {
    for (const listener of Array.from(authSessionEndedListeners)) {
        try {
            listener(code);
        }
        catch {
            // One listener must not stop the others.
        }
    }
}
function getErrorMessage(body, fallback) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        return fallback;
    }
    const errorBody = body;
    const summary = typeof errorBody.error === 'string'
        ? errorBody.error
        : typeof errorBody.message === 'string' ? errorBody.message : fallback;
    if (summary !== 'Validation failed'
        || !errorBody.details
        || typeof errorBody.details !== 'object'
        || Array.isArray(errorBody.details)) {
        return summary;
    }
    const firstDetail = Object.entries(errorBody.details)
        .find(([, value]) => typeof value === 'string' && value.trim());
    if (!firstDetail) {
        return summary;
    }
    const [field, detail] = firstDetail;
    const fieldPrefix = field && field !== '_root' ? `${field}: ` : '';
    return `${summary} — ${fieldPrefix}${detail}`;
}
function getErrorCode(body) {
    if (!body || typeof body !== 'object' || Array.isArray(body)) {
        return undefined;
    }
    const { code } = body;
    return typeof code === 'string' && code ? code : undefined;
}
/** Compatibility wrapper for callers that only need a successfully refreshed token. */
export async function refreshAuthToken() {
    const result = await refreshAuthSession();
    return result.status === 'refreshed' ? result.token : null;
}
/** Share rotation only within the originating session; never share it with a new login. */
export function refreshAuthSession() {
    const identity = getApiSessionIdentity();
    const refreshToken = getRefreshToken();
    const origin = getApiOrigin();
    const generation = getStorageGeneration();
    const owner = JSON.stringify([origin, identity, refreshToken, generation]);
    if (refreshInFlight?.owner === owner)
        return refreshInFlight.promise;
    const promise = doRefreshAuthSession({ identity, refreshToken, origin, generation }).finally(() => {
        if (refreshInFlight?.promise === promise)
            refreshInFlight = null;
    });
    refreshInFlight = { owner, promise };
    return promise;
}
async function doRefreshAuthSession(context) {
    const { identity, refreshToken, origin, generation } = context;
    const envelope = getAuthTokenEnvelope();
    if (!refreshToken)
        return { status: 'rejected', code: 'AUTH_REQUIRED' };
    const current = () => generation === getStorageGeneration() && origin === getApiOrigin()
        && identity === getApiSessionIdentity() && refreshToken === getRefreshToken();
    const controller = new AbortController();
    let timer;
    try {
        // Bound both the response and its body. A fetch implementation that ignores
        // abort must not write credentials after this deadline has already expired.
        const { response, data } = await Promise.race([
            (async () => {
                const response = await fetch(`${origin}/api/auth/refresh`, {
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ refreshToken }), method: 'POST', signal: controller.signal
                });
                return { response, data: await response.json() };
            })(),
            new Promise((_, reject) => {
                timer = setTimeout(() => { controller.abort(); reject(new Error('Refresh timed out.')); }, 15000);
            })
        ]);
        if (!current())
            return { status: 'superseded' };
        if (!response.ok) {
            const code = getErrorCode(data);
            if ((response.status === 401 || response.status === 403) && code && REFRESH_REJECTION_CODES.has(code)) {
                announceAuthSessionEnded(code);
                return { status: 'rejected', code };
            }
            return { status: 'unavailable' };
        }
        if (!data || typeof data !== 'object' || !('token' in data)
            || typeof data.token !== 'string' || !data.token.trim()
            || ('refreshToken' in data && (typeof data.refreshToken !== 'string' || !data.refreshToken.trim()))) {
            return { status: 'unavailable' };
        }
        setSecureItem('vrs_auth_token', JSON.stringify({ ...(envelope || {}), token: data.token, refreshedAt: Date.now() }));
        if ('refreshToken' in data && typeof data.refreshToken === 'string')
            setSecureItem('vrs_refresh_token', data.refreshToken);
        completedRefresh = { identity, origin, generation, token: data.token, refreshToken: getRefreshToken() };
        return { status: 'refreshed', token: data.token };
    }
    catch {
        return { status: current() ? 'unavailable' : 'superseded' };
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
/** The installation's id from notification registration; it outlives sign-out. */
function notificationDeviceIdForSignOut() {
    const id = getPersistentItem('malka_notification_device_id');
    return id && id.length >= 8 && id.length <= 160 ? id : null;
}
/** Freeze before retiring local credentials; retries must revoke this same session. */
export function captureAuthRevocation() {
    return { origin: getApiOrigin(), refreshToken: getRefreshToken(), notificationDeviceId: notificationDeviceIdForSignOut() };
}
export async function revokeAuthSession(request = captureAuthRevocation()) {
    const { origin, refreshToken, notificationDeviceId } = request;
    if (!refreshToken) {
        return { confirmed: true };
    }
    const controller = new AbortController();
    let timer;
    try {
        if (origin !== getApiOrigin())
            throw new Error('Sign-out origin changed.');
        await Promise.race([
            (async () => {
                const response = await fetch(`${origin}/api/auth/logout`, {
                    body: JSON.stringify(notificationDeviceId ? { refreshToken, notificationDeviceId } : { refreshToken }),
                    headers: { 'Content-Type': 'application/json' },
                    method: 'POST', signal: controller.signal
                });
                const receipt = await response.json();
                if (!response.ok || !receipt || typeof receipt !== 'object'
                    || !('success' in receipt) || receipt.success !== true) {
                    throw new Error('Sign-out acknowledgement missing.');
                }
            })(),
            new Promise((_, reject) => {
                timer = setTimeout(() => { controller.abort(); reject(new Error('Sign-out timed out.')); }, 15000);
            })
        ]);
        return { confirmed: true };
    }
    catch {
        return { confirmed: false, code: 'SIGN_OUT_UNCONFIRMED' };
    }
    finally {
        if (timer)
            clearTimeout(timer);
    }
}
// The last token that a refresh before a request gave. A device clock that is
// far ahead reads even a new token as expired; it is not refreshed again.
let lastTokenRefreshedBeforeRequest = null;
function sessionChangedResult() {
    return { data: null, status: 409, code: 'AUTH_SESSION_CHANGED', error: 'Your session changed. Try again.' };
}
/** Synchronous, so a request with a current token reads its session when it is called. */
function accessTokenNeedsRefresh() {
    const token = getAuthToken();
    return Boolean(token && token !== lastTokenRefreshedBeforeRequest && getRefreshToken() && isAccessTokenExpired(token));
}
/**
 * An access token that is already expired (12 h) is refreshed before the
 * request, not after its 401. A request that may not refresh after a 401
 * (retryAuth: false, for example Settings) then also works after the expiry,
 * instead of "Session expired. Sign in again." while the person is signed in.
 * The refresh keeps the same session, and only one runs at a time
 * (refreshAuthSession). Null: send the request.
 *
 * @param {AbortSignal} [signal] - The request's signal.
 * @returns {Promise<ApiResponse|null>} The result for the caller, or null.
 */
async function refreshExpiredTokenBeforeRequest(signal) {
    const origin = getApiOrigin();
    const generation = getStorageGeneration();
    const refreshed = await refreshAuthSession();
    if (refreshed.status === 'superseded' || origin !== getApiOrigin() || generation !== getStorageGeneration()) {
        return sessionChangedResult();
    }
    if (signal?.aborted) {
        return { data: null, status: 0, code: 'REQUEST_ABORTED', error: 'Request cancelled.' };
    }
    if (refreshed.status === 'refreshed') {
        lastTokenRefreshedBeforeRequest = refreshed.token;
        return getAuthToken() === refreshed.token ? null : sessionChangedResult();
    }
    if (refreshed.status === 'unavailable') {
        return { data: null, status: 503, code: 'AUTH_REFRESH_UNAVAILABLE', error: 'No connection. Try again.' };
    }
    if (refreshed.status === 'rejected') {
        // The server refused the refresh: the session is over, as its 401 would say.
        return { data: null, status: 401, code: refreshed.code, error: 'Session expired. Sign in again.' };
    }
    return sessionChangedResult();
}
async function request(method, path, body, options = {}, retried = false) {
    if (options.auth !== 'none' && !retried && !NO_REFRESH_RETRY_RE.test(path) && accessTokenNeedsRefresh()) {
        const early = await refreshExpiredTokenBeforeRequest(options.signal);
        if (early) {
            return early;
        }
    }
    const baseUrl = getBaseUrl();
    const token = options.auth === 'none' ? null : getAuthToken();
    const identity = getApiSessionIdentity();
    const origin = getApiOrigin();
    const generation = getStorageGeneration();
    // A recorded VideoMail is posted as the file itself; everything else is
    // JSON. Keeping both on this path means uploads inherit the same auth,
    // session-identity and refresh behaviour as every other call.
    const isBinaryBody = typeof Blob !== 'undefined' && body instanceof Blob;
    const headers = {
        'Content-Type': isBinaryBody
            ? options.contentType || body.type || 'application/octet-stream'
            : 'application/json'
    };
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    try {
        const response = await fetch(resolveUrl(baseUrl, path), {
            body: isBinaryBody ? body : body ? JSON.stringify(body) : undefined,
            headers,
            method,
            signal: options.signal,
            ...(options.auth === 'none' ? { credentials: 'same-origin' } : {}),
            ...(options.keepalive ? { keepalive: true } : {})
        });
        const status = response.status;
        if (response.status === 401
            && options.auth !== 'none'
            && options.retryAuth !== false
            && !retried
            && !NO_REFRESH_RETRY_RE.test(path)
            && (token || getRefreshToken())) {
            const sessionChanged = () => sessionChangedResult();
            if (origin !== getApiOrigin() || generation !== getStorageGeneration())
                return sessionChanged();
            if (identity !== getApiSessionIdentity()) {
                if (completedRefresh?.identity !== identity || completedRefresh.origin !== origin
                    || completedRefresh.generation !== generation || completedRefresh.token !== getAuthToken()
                    || completedRefresh.refreshToken !== getRefreshToken())
                    return sessionChanged();
                if (options.signal?.aborted)
                    return { data: null, status: 0, code: 'REQUEST_ABORTED', error: 'Request cancelled.' };
                return request(method, path, body, options, true);
            }
            const refreshed = await refreshAuthSession();
            if (refreshed.status === 'superseded' || origin !== getApiOrigin() || generation !== getStorageGeneration()
                || (refreshed.status !== 'refreshed' && identity !== getApiSessionIdentity())) {
                return sessionChanged();
            }
            if (refreshed.status === 'refreshed') {
                if (getAuthToken() !== refreshed.token)
                    return sessionChanged();
                if (options.signal?.aborted)
                    return { data: null, status: 0, code: 'REQUEST_ABORTED', error: 'Request cancelled.' };
                return request(method, path, body, options, true);
            }
            if (refreshed.status === 'unavailable') {
                return { data: null, status: 503, code: 'AUTH_REFRESH_UNAVAILABLE',
                    error: 'No connection. Try again.' };
            }
        }
        if (!response.ok) {
            let errorMessage = `HTTP ${status}`;
            let code;
            let errorBody;
            try {
                const errBody = await response.json();
                errorMessage = getErrorMessage(errBody, errorMessage);
                code = getErrorCode(errBody);
                if (options.includeErrorBody) {
                    errorBody = errBody;
                }
            }
            catch {
                // non-JSON error body
            }
            return { code, data: null, error: errorMessage, status, ...errorBody === undefined ? {} : { errorBody } };
        }
        // 204 No Content
        if (status === 204) {
            return { data: null, error: null, status };
        }
        // A file download: the bytes themselves, with the same auth, refresh
        // and error handling as every JSON call.
        if (options.responseType === 'blob') {
            return { data: await response.blob(), error: null, status };
        }
        let data;
        try {
            data = await response.json();
        }
        catch {
            return {
                data: null,
                error: `Invalid JSON response (HTTP ${status})`,
                status
            };
        }
        return { data, error: null, status };
    }
    catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Network error';
        return {
            data: null,
            error: errorMessage,
            status: 0
        };
    }
}
export function unwrapApiResponse(response) {
    if (response.error) {
        throw new Error(response.error);
    }
    if (response.data === null) {
        throw new Error(`API returned no data (HTTP ${response.status})`);
    }
    return response.data;
}
export const apiClient = {
    get: (path, options) => request('GET', path, undefined, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    patch: (path, body, options) => request('PATCH', path, body, options),
    del: (path, options) => request('DELETE', path, undefined, options)
};
